import { useCallback, useMemo } from "react"
import {
   createColumnHelper,
   getCoreRowModel,
   useReactTable,
   flexRender,
} from "@tanstack/react-table"
import { QueryRender } from "@/components/helpers/QueryRender"
import { useYoungs } from "@/entities/user/useYoung"
import { useUpdateUser } from "@/entities/user/useUser"
import { useGlobalSettings } from "@/entities/globalSettings/useGlobalSettings"
import { UserAvatar } from "@/components/user/UserAvatar"
import NouvelleAnneeSteps from "./NouvelleAnneeSteps"
import { Link } from "react-router-dom"

interface Young {
   id: string
   firstName: string
   lastName: string
   group?: string
   is_active: boolean
   photo?: string
}

export default function NouvelleAnneeConfigurerGroupes() {
   const { data: youngs, ...query } = useYoungs()
   const updateUser = useUpdateUser()
   const globalSettingsQuery = useGlobalSettings()

   const handleGroupChange = useCallback(
      async (young: Young, newGroup: string) => {
         await updateUser(young.id, { group: newGroup }, { quickMode: true })
      },
      [updateUser],
   )

   const handleDeactivate = useCallback(
      async (young: Young) => {
         await updateUser(young.id, {
            group: "",
            is_active: false,
         })
      },
      [updateUser],
   )

   const columnHelper = createColumnHelper<Young>()
   const columns = useMemo(
      () => [
         columnHelper.accessor("photo", {
            header: "",
            cell: (info) => (
               <UserAvatar
                  photo={info.getValue()}
                  name={`${info.row.original.firstName} ${info.row.original.lastName}`}
                  size="sm"
               />
            ),
         }),
         columnHelper.accessor("firstName", {
            header: "Prénom",
         }),
         columnHelper.accessor("lastName", {
            header: "Nom",
         }),
         columnHelper.accessor("group", {
            header: "Groupe",
            cell: (info) => (
               <div className="flex flex-wrap gap-2">
                  <QueryRender query={globalSettingsQuery}>
                     {(settings) =>
                        settings.groups.map((group) => (
                           <button
                              key={group}
                              onClick={() => handleGroupChange(info.row.original, group)}
                              className={`btn btn-xs ${
                                 info.getValue() === group ? "btn-primary" : "btn-ghost"
                              }`}
                           >
                              {group}
                           </button>
                        ))
                     }
                  </QueryRender>
               </div>
            ),
         }),
         columnHelper.display({
            id: "actions",
            cell: (info) => (
               <button
                  onClick={() => handleDeactivate(info.row.original)}
                  className="btn btn-xs btn-error"
                  disabled={!info.row.original.is_active}
               >
                  Désactiver
               </button>
            ),
         }),
      ],
      [handleGroupChange, handleDeactivate, globalSettingsQuery],
   )

   const table = useReactTable({
      data: youngs || [],
      columns,
      getCoreRowModel: getCoreRowModel(),
   })

   return (
      <div className="container mx-auto p-4">
         <NouvelleAnneeSteps step={2} />

         <h1 className="text-2xl font-bold mb-6">Configurer les groupes</h1>

         <QueryRender query={query} data={youngs}>
            {() => (
               <div className="overflow-x-auto">
                  <table className="table">
                     <thead>
                        {table.getHeaderGroups().map((headerGroup) => (
                           <tr key={headerGroup.id}>
                              {headerGroup.headers.map((header) => (
                                 <th key={header.id}>
                                    {header.isPlaceholder
                                       ? null
                                       : flexRender(
                                            header.column.columnDef.header,
                                            header.getContext(),
                                         )}
                                 </th>
                              ))}
                           </tr>
                        ))}
                     </thead>
                     <tbody>
                        {table.getRowModel().rows.map((row) => (
                           <tr key={row.id}>
                              {row.getVisibleCells().map((cell) => (
                                 <td key={cell.id}>
                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                 </td>
                              ))}
                           </tr>
                        ))}
                     </tbody>
                  </table>
               </div>
            )}
         </QueryRender>

         <Link to="/" className="btn btn-primary mt-6 mx-auto">
            Terminer
         </Link>
      </div>
   )
}
